import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/navigation';

import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useCheckSpotOnly } from '@flipster/features/src/shared/regulation';
import { Button } from '@flipster/react/design-system';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { grid, hstack } from '@flipster/styled-system/patterns';
import { Usd1Currency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { Usd1PointsEvent } from '@flipster/utils/segment/events/usd1-points';

import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';

const HoldSection = () => {
  const router = useRouter();
  const { isLoggedIn, user } = useAuth();
  const isSpotOnly = useCheckSpotOnly();
  const { openWalletModal } = useWalletModal();
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { maxAprPct, appliedAprPct } = getEarnSummaryOfCurrency(Usd1Currency, earnSummary);
  const showPersonalizedApr = user?.firstDepositAt;
  const displayedAprPct = showPersonalizedApr ? appliedAprPct : maxAprPct;

  return (
    <section className={css({ mt: 48 })}>
      <h3 className={css({ textStyle: 'Title/M' })}>Hold</h3>
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
        {!isSpotOnly ? (
          <>
            Rack up points while you hold USD1 assets, also earn up to{' '}
            <Formatted.Percent value={maxAprPct} precision={2} toFixed /> APR.
          </>
        ) : (
          'Rack up points while you hold USD1 assets.'
        )}
      </p>
      <div>
        <div className={grid({ columns: { base: 1, tablet: 2 }, mt: 8, gridColumnGap: 40 })}>
          <div
            className={hstack({
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              borderColor: 'border.default.level-1',
              py: 16,
            })}
          >
            <CurrencyDisplay
              currency={Usd1Currency}
              mainLabel={
                <div className={hstack({ gap: 4, textStyle: 'Text/L/Strong' })}>
                  <CurrencyDisplay.Label value='shortName' />
                  {!isSpotOnly && <span>Earn</span>}
                </div>
              }
              subLabel={
                !isSpotOnly && (
                  <div className={hstack({ gap: 4, textStyle: 'Text/M/Monospace/Regular' })}>
                    <Formatted.Percent value={displayedAprPct} precision={2} toFixed />
                    <span>APR</span>
                  </div>
                )
              }
              icon={<CurrencyDisplay.Icon size={24} space={8} />}
            />
            <Button
              size='medium'
              label='Deposit'
              css={{ minW: 156 }}
              onClick={() => {
                segment.track(Usd1PointsEvent.Usd1PageDepositClick);
                if (!isLoggedIn) {
                  router.push('/login');
                  return;
                }
                openWalletModal('deposit', { defaultCurrency: Usd1Currency });
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HoldSection;
